import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { AiOverview, AiOverviewPending } from './ai-overview'
import { classifySearchQuery, type SearchMode } from './search-mode'
import { SearchModeSwitch } from './search-mode-switch'
import { SearchTips } from './search-tips'
import type { SearchOverviewChat } from './storefront-chat-api'
import {
  autocompleteQueryOptions,
  type AutocompleteLimits,
  type StorefrontScope,
} from './storefront-api'

const DEBOUNCE_MS = 100
// The overview waits for a pause in typing; each one is a model call.
const OVERVIEW_DEBOUNCE_MS = 700
const OVERVIEW_MIN_LENGTH = 3
const MIN_QUERY_LENGTH = 2
const LARGE_LIMITS: AutocompleteLimits = {
  products: 4,
  vendors: 4,
  categories: 6,
  suggestions: 10,
}
const SMALL_LIMITS: AutocompleteLimits = {
  products: 4,
  vendors: 4,
  categories: 5,
  suggestions: 5,
}
// With an overview below them the lists shrink so the dropdown still fits the screen (USA-1247).
const OVERVIEW_LIMITS: AutocompleteLimits = {
  products: 2,
  vendors: 2,
  categories: 3,
  suggestions: 3,
}

type AutocompleteOption =
  | { kind: 'product'; label: string; model: string; thumbnail: string | null }
  | { kind: 'suggestion'; label: string }
  | { kind: 'category' | 'vendor'; label: string; shopifyId: string }

export function HighlightedText({ text, query }: { text: string; query: string }) {
  const index = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1
  if (index === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, index)}
      <mark className="sf-suggest__match">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  )
}

export function StorefrontAutocomplete({
  value,
  onValueChange,
  open,
  onOpenChange,
  onSearch,
  onScope,
  largeScreen,
  narrow = false,
  mode,
  onModeChange,
  onFollowUp,
}: {
  value: string
  onValueChange: (value: string) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Without a mode the page uses the switch's mode for this text. */
  onSearch: (query: string, mode?: SearchMode) => void
  onScope: (scope: StorefrontScope) => void
  largeScreen: boolean
  /** Phone width: the tips and mode switch move to a row under the field so the text has room. */
  narrow?: boolean
  mode: SearchMode
  onModeChange: (mode: SearchMode) => void
  onFollowUp?: (chat: SearchOverviewChat) => void
}) {
  const idPrefix = useId()
  const rootRef = useRef<HTMLFormElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const fetchLimits = largeScreen ? LARGE_LIMITS : SMALL_LIMITS
  const normalized = value.trim().toLowerCase()
  // The overview's slot is held from the first word it can answer, so nothing jumps when it starts.
  const withOverview = mode === 'ai' && normalized.length >= OVERVIEW_MIN_LENGTH
  const limits = withOverview ? OVERVIEW_LIMITS : fetchLimits
  const debounced = useDebouncedValue(normalized, DEBOUNCE_MS)
  const ready = debounced.length >= MIN_QUERY_LENGTH && debounced === normalized

  const autocomplete = useQuery({
    ...autocompleteQueryOptions(debounced, fetchLimits),
    enabled: open && debounced.length >= MIN_QUERY_LENGTH,
  })
  const overviewQuery = useDebouncedValue(normalized, OVERVIEW_DEBOUNCE_MS)
  const overviewReady =
    open &&
    mode === 'ai' &&
    overviewQuery === normalized &&
    overviewQuery.length >= OVERVIEW_MIN_LENGTH
  const data = ready ? autocomplete.data : undefined
  const loading = normalized.length >= MIN_QUERY_LENGTH && !data && !autocomplete.isError

  const products: AutocompleteOption[] = (data?.products ?? [])
    .filter((item) => item.title.trim() && item.url.trim())
    .slice(0, limits.products)
    .map((item) => ({
      kind: 'product',
      label: item.title,
      model: item.model,
      thumbnail: item.thumbnail,
    }))
  const suggestions: AutocompleteOption[] = (data?.suggestions ?? [])
    .filter((item) => item.text.trim())
    .slice(0, limits.suggestions)
    .map((item) => ({ kind: 'suggestion', label: item.text.trim() }))
  const categories: AutocompleteOption[] = (data?.categories ?? [])
    .slice(0, limits.categories)
    .map((item) => ({
      kind: 'category',
      label: item.title,
      shopifyId: item.shopify_id,
    }))
  const vendors: AutocompleteOption[] = (data?.vendors ?? [])
    .slice(0, limits.vendors)
    .map((item) => ({
      kind: 'vendor',
      label: item.title,
      shopifyId: item.shopify_id,
    }))
  const options = [...products, ...suggestions, ...categories, ...vendors]
  const optionId = (index: number) => `${idPrefix}-option-${index}`
  const showPanel = open && normalized.length > 0

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, onOpenChange])

  const close = () => {
    setActiveIndex(-1)
    onOpenChange(false)
    inputRef.current?.blur()
  }

  const choose = (option: AutocompleteOption) => {
    close()
    if (option.kind === 'product') {
      onValueChange(option.model)
      onSearch(option.model, classifySearchQuery(option.model))
    } else if (option.kind === 'suggestion') {
      onValueChange(option.label)
      onSearch(option.label)
    } else {
      onValueChange('')
      onScope({
        key: option.kind === 'vendor' ? 'manufacturer' : 'category',
        value: option.shopifyId,
        label: option.label,
      })
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    close()
    onSearch(value.trim())
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      close()
      return
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
      event.preventDefault()
      onOpenChange(true)
      const step = event.key === 'ArrowDown' ? 1 : -1
      const next = activeIndex === -1 ? (step === 1 ? 0 : options.length - 1) : activeIndex + step
      setActiveIndex((next + options.length) % options.length)
      return
    }
    if (event.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault()
      choose(options[activeIndex])
    }
  }

  const renderOption = (option: AutocompleteOption) => {
    const index = options.indexOf(option)
    const props = {
      id: optionId(index),
      role: 'option' as const,
      'aria-selected': index === activeIndex,
      tabIndex: -1,
      onPointerDown: (event: { preventDefault: () => void }) => event.preventDefault(),
      onClick: () => choose(option),
    }
    if (option.kind === 'product') {
      return (
        <li key={`product-${index}`} role="presentation">
          <button
            type="button"
            className="sf-suggest__product"
            data-e2e="autocomplete-product"
            {...props}
          >
            {option.thumbnail ? (
              <img src={option.thumbnail} alt="" width={48} height={48} loading="lazy" />
            ) : (
              <span className="sf-suggest__product-blank" aria-hidden="true" />
            )}
            <span className="sf-suggest__product-copy">
              <span className="sf-suggest__product-model">{option.model}</span>
              <span className="sf-suggest__product-title">{option.label}</span>
            </span>
          </button>
        </li>
      )
    }
    return (
      <li key={`${option.kind}-${index}`} role="presentation">
        <button
          type="button"
          className={`sf-suggest__link ${option.kind === 'suggestion' ? 'sf-suggest__link--suggestion' : ''}`}
          data-e2e={option.kind === 'suggestion' ? 'autocomplete-suggestion' : undefined}
          {...props}
        >
          <span>
            <HighlightedText text={option.label} query={normalized} />
          </span>
        </button>
      </li>
    )
  }

  const statusText = !showPanel
    ? ''
    : normalized.length < MIN_QUERY_LENGTH
      ? 'What can we help you find?'
      : autocomplete.isError
        ? 'Suggestions are unavailable. Press Enter to search.'
        : loading
          ? 'Searching...'
          : options.length
            ? `${options.length} suggestions available. Use the up and down arrow keys to review them.`
            : `No matching suggestions for ${normalized}. Press Enter to search.`

  const tips = (
    <SearchTips
      className="sf-header-search__tips"
      onOpen={close}
      onPick={(query) => {
        onValueChange(query)
        onSearch(query)
      }}
    />
  )
  const modeSwitch = <SearchModeSwitch mode={mode} onModeChange={onModeChange} />

  return (
    <form ref={rootRef} role="search" className="sf-header-search" onSubmit={submit}>
      <div className="sf-header-search__field">
        <button type="submit" className="sf-header-search__icon" aria-label="Search">
          <Search size={18} strokeWidth={1.75} />
        </button>
        <input
          ref={inputRef}
          type="search"
          className="sf-header-search__input"
          value={value}
          placeholder="Search products, brands, categories..."
          role="combobox"
          aria-label="Search"
          aria-expanded={showPanel}
          aria-controls={`${idPrefix}-listbox`}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          maxLength={120}
          data-e2e="site-search"
          onFocus={() => onOpenChange(true)}
          onChange={(event) => {
            onValueChange(event.target.value)
            setActiveIndex(-1)
            onOpenChange(true)
          }}
          onKeyDown={onKeyDown}
        />
        {narrow ? null : (
          <>
            {tips}
            {modeSwitch}
          </>
        )}
        {value ? (
          <button
            type="button"
            className="sf-header-search__clear"
            aria-label="Clear search"
            onClick={() => {
              onValueChange('')
              inputRef.current?.focus()
            }}
          >
            <X size={18} />
          </button>
        ) : null}
      </div>
      {narrow ? (
        <div className="sf-header-search__tools">
          {modeSwitch}
          {tips}
        </div>
      ) : null}

      {showPanel ? (
        <div
          id={`${idPrefix}-listbox`}
          role="listbox"
          aria-label="Search suggestions"
          className={`sf-suggest ${withOverview ? 'sf-suggest--overview' : ''}`}
        >
          {normalized.length < MIN_QUERY_LENGTH ? (
            <p className="sf-suggest__message">What can we help you find?</p>
          ) : (
            <>
              <div className="sf-suggest__lists" role="presentation">
                <div className="sf-suggest__main" role="presentation">
                  {products.length ? (
                    <div role="group" aria-label="Products">
                      <p className="sf-suggest__heading" aria-hidden="true">
                        Products
                      </p>
                      <ul className="sf-suggest__products" role="presentation">
                        {products.map(renderOption)}
                      </ul>
                    </div>
                  ) : null}
                  <div role="group" aria-label="Search suggestions">
                    <p className="sf-suggest__heading" aria-hidden="true">
                      Search suggestions
                    </p>
                    {loading ? (
                      <SuggestSkeleton count={Math.min(4, limits.suggestions)} />
                    ) : autocomplete.isError ? (
                      <p className="sf-suggest__message">
                        Suggestions are unavailable. Press Enter to search.
                      </p>
                    ) : options.length ? (
                      <ul className="sf-suggest__list" role="presentation">
                        {suggestions.map(renderOption)}
                      </ul>
                    ) : (
                      <p className="sf-suggest__message">
                        No matching suggestions. Press Enter to search.
                      </p>
                    )}
                  </div>
                </div>
                {loading || categories.length || vendors.length ? (
                  <div className="sf-suggest__side" role="presentation">
                    {loading || categories.length ? (
                      <div role="group" aria-label="Categories">
                        <p className="sf-suggest__heading" aria-hidden="true">
                          Categories
                        </p>
                        {loading ? (
                          <SuggestSkeleton count={3} />
                        ) : (
                          <ul className="sf-suggest__list" role="presentation">
                            {categories.map(renderOption)}
                          </ul>
                        )}
                      </div>
                    ) : null}
                    {loading || vendors.length ? (
                      <div role="group" aria-label="Vendors">
                        <p className="sf-suggest__heading" aria-hidden="true">
                          Vendors
                        </p>
                        {loading ? (
                          <SuggestSkeleton count={Math.min(3, limits.vendors)} />
                        ) : (
                          <ul className="sf-suggest__list" role="presentation">
                            {vendors.map(renderOption)}
                          </ul>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {withOverview ? (
                <div className="sf-suggest__ai" role="presentation">
                  {overviewReady ? (
                    <AiOverview
                      request={{ query: overviewQuery, placement: 'autocomplete' }}
                      onSearch={(query) => {
                        close()
                        onValueChange(query)
                        onSearch(query, classifySearchQuery(query))
                      }}
                      onFollowUp={
                        onFollowUp
                          ? (chat) => {
                              close()
                              onFollowUp(chat)
                            }
                          : undefined
                      }
                    />
                  ) : (
                    <AiOverviewPending />
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {statusText}
      </span>
    </form>
  )
}

function SuggestSkeleton({ count }: { count: number }) {
  return (
    <ul className="sf-suggest__list" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <li key={index} className="sf-suggest__skeleton">
          <span />
          <span className="is-short" />
        </li>
      ))}
    </ul>
  )
}
